import { useQuery } from '@apollo/client';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import React from 'react';

import { LoadingIcon } from '../components/LoadingIcon';
import { VIDEO_TYPE } from '../mediaplayers/MediaPlayer/types';
import atInternetId from '../util/atInternetId';
import createErrorMediaData from '../util/createErrorMediaData';
import personalAdConsent from '../util/personalAdConsent';
import getSitenameFromUrl from '../util/sitenameFromUrl';
import getMedia from './queries/media.graphql';

/**
 * This is a temporary transform function until we receive final data from the backend.
 * Missing data will be populated with mock data.
 *
 * @param data
 * @returns object
 */
const createMediaData = ({ mediaId, player, userId = null }) => {
  const mediaData = {
    mediaId,
    userId,
    type: player.type,
    title: player.title,
  };

  document.title = player.title;

  if (player.token) {
    return {
      ...mediaData,
      embedId: player.token,
      isBroadcastEnabled:
        player.brand === null ? true : player.brand.broadcastsEnabled,
    };
  }

  if (mediaData.type === VIDEO_TYPE) {
    return {
      ...mediaData,
      prid: null,
      duration: player.duration,
      programTitle: player.programTitle,
      genres: player.genres,
      site: getSitenameFromUrl(window.referrer || document.location.href),
      playername: 'bnnvara-web',
      classifications: player.classifications,
      broadcasters: player.broadcasters
        .map((broadcaster) => broadcaster.name.toLowerCase())
        .join(','),
      poster: get(player, 'image.url', null),
      source: player.sources.map((source) => ({
        src: source.url,
        type: source.type || 'video/mp4',
        name: source.name || 'HD',
        ratio: source.ratio || '16:9',
      })),
      cta: {
        title: player.cta?.title,
        url: player.cta?.url,
      },
      subtitles:
        player.subtitles[0]?.url.includes('.vtt') &&
        player.subtitles?.map((subtitle) => ({
          kind: 'subtitles',
          src: subtitle.url,
          label: subtitle.language,
          // make sure that no subtitle is checked until user chooses a subtitle
          default: false,
        })),
    };
  }

  // Then it must be audio
  return {
    ...mediaData,
    prid: null,
    duration: player.duration,
    programTitle: player.programTitle,
    genres: player.genres,
    site: getSitenameFromUrl(window.referrer || document.location.href),
    playername: 'bnnvara-web',
    classifications: player.classifications,
    broadcasters: player.broadcasters
      .map((broadcaster) => broadcaster.name.toLowerCase())
      .join(','),
    poster: null,
    source: player.sources.map((source) => ({
      src: source.url,
      type: source.type || 'audio/mp3',
      name: source.name || 'audio',
      ratio: source.ratio || '12:1',
    })),
    cta: null,
    subtitles: null,
  };
};

/**
 * This component expects an id and a render prop function.
 * Once the data is loaded it will use the render prop to render its children.
 */

export const MediaDataQuery = ({ id, userId = null, render }) => {
  /**
   * Get Media query
   *
   * Query vars:
   * @mediaId
   * @hasAdConsent
   * @atinternetId
   */
  const {
    loading: loadingMediaData,
    data: mediaData,
    error: errorMediaData,
  } = useQuery(getMedia, {
    variables: {
      id,
      hasAdConsent: personalAdConsent(),
      atInternetId: atInternetId(),
    },
  });

  if (loadingMediaData) return <LoadingIcon />;

  const media = errorMediaData
    ? createErrorMediaData(errorMediaData)
    : createMediaData({
        mediaId: id,
        userId,
        ...mediaData,
      });

  return render(media);
};

MediaDataQuery.propTypes = {
  id: PropTypes.string.isRequired,
  render: PropTypes.func.isRequired,
  userId: PropTypes.string,
};
